<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Entradas de Produtos</h5>
      <BButton
        v-if="hasPermission('adicionar_entradas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
          <i class="bi bi-plus-circle-fill" title="Nova Entrada"></i>
      </BButton>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <select v-model="filters.quando" class="form-control same-height" @change="filtrar">
        <option value="Todos">Todos</option>
        <option value="Hoje">Hoje</option>
        <option value="Anteriores">Anteriores</option>
      </select>
      <input
        v-model="filters.fornecedor"
        @input="filtrar"
        type="text"
        class="form-control same-height"
        placeholder="Filtrar por Fornecedor"
      />
      <select v-model="filters.status" class="form-control same-height" @change="filtrar">
        <option value="Todos">Todos</option>
        <option value="Pendentes">Pendentes</option>
        <option value="Finalizadas">Finalizadas</option>
      </select>
      <b-button variant="secondary" @click="limparFiltros" class="d-flex align-items-center">
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela nativa com ordenação -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th><button class="btn btn-link p-0" @click="sort('id')"># {{ arrow('id') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('fornecedor_nome')">Fornecedor {{ arrow('fornecedor_nome') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('data_entrada')">Data Entrada {{ arrow('data_entrada') }}</button></th>
          <th class="text-center"><button class="btn btn-link p-0" @click="sort('valor_total')">Valor Total {{ arrow('valor_total') }}</button></th>
          <th><button class="btn btn-link p-0" @click="sort('pendente')">Status {{ arrow('pendente') }}</button></th>
          <th class="text-end">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in items" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.fornecedor_nome }}</td>
          <td>{{ formatDate(item.data_entrada) }}</td>
          <td class="text-center">R$ {{ formatDecimal(item.valor_total) }}</td>
          <td>
            <span :class="item.pendente ? 'text-danger' : 'text-success'">
              {{ item.pendente ? 'Pendente' : 'Finalizada' }}
            </span>
          </td>
          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_entradas')"
              class="bi bi-eye-fill text-primary me-2"
              title="Detalhar"
              @click="detalhar(item.id)"
            />
            <i
              v-if="hasPermission('alterar_entradas')"
              class="bi bi-pencil-fill text-warning me-2"
              title="Editar"
              @click="editar(item.id)"
            />
            <i
              v-if="hasPermission('excluir_entradas')"
              class="bi bi-trash-fill text-danger me-2"
              title="Excluir"
              @click="excluir(item.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <!-- resumo -->
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <!-- seletor de itens por página -->
      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
        />
      </div>

      <!-- controle de página -->
      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, computed, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useSortableTable } from '@/utils/useSortableTable';
import { usePermissions } from '@/composables/usePermissions';
import { BButton, BFormSelect, BPagination } from 'bootstrap-vue-next';
import axios from '@/services/axios';
import globalUtils from '@/utils/globalUtils';

const router = useRouter();
const { fetchPermissions, hasPermission } = usePermissions();

// composable para paginação, filtros e ordenação
const {
  items,
  totalItems,
  page,
  limit,
  filters,
  sort,
  arrow,
  load,
} = useSortableTable('/loja/entradas', { key: 'data_entrada', order: 'asc' });

// inicializa filtros
Object.assign(filters.value, {
  quando: 'Todos',
  fornecedor: '',
  status: 'Todos'
});

// opções de limite de página
const pageOptions = [10, 15, 20, 25, 50];

// computeds para resumo
const startRow = computed(() =>
  (page.value - 1) * limit.value + 1
);
const endRow = computed(() =>
  Math.min(page.value * limit.value, totalItems.value)
);

// watchers para recarregar ao mudar página ou limite
watch(page, () => load());
watch(limit, () => {
  page.value = 1;
  load();
});

// formatação
function formatDecimal(val) {
  const num = Number(val);
  if (isNaN(num)) return '';
  return num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dt) {
  const formatted = globalUtils.formatarData(dt);
  return formatted || '—';
}

// navegação e CRUD
function irParaCadastro() { router.push('/loja/entradas/novo'); }
function detalhar(id)        { router.push(`/loja/entradas/${id}/detalhar`); }
function editar(id)          { router.push(`/loja/entradas/${id}`); }
async function excluir(id) {
  if (!confirm('Confirma exclusão da entrada?')) return;
  try {
    await axios.delete(`/loja/entradas/${id}`);
    load();
    alert('Entrada excluída');
  } catch (e) {
    alert(e.response?.data?.error || 'Erro ao excluir entrada');
  }
}

// filtros e paginação local
function filtrar() {
  page.value = 1;
  load();
}
function limparFiltros() {
  page.value = 1;
  Object.assign(filters.value, {
    quando: 'Todos',
    fornecedor: '',
    status: 'Todos'
  });
  load();
}

onMounted(async () => {
  await fetchPermissions();
  load();
});
</script>

<style scoped>
.same-height { height: 30px; }

:deep(.pagination.pagination-sm .page-link) {
  padding: rem rem;
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

</style>
